import React, { useState, useEffect } from 'react';
import { Users, Phone, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import administratorsData from '../data/administrators.json';

function Administrators() {
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedDivisions, setExpandedDivisions] = useState({});

  useEffect(() => {
    setLoading(true);
    
    // Transform the JSON data to add static image paths
    const transformedData = administratorsData.map(division => ({
      ...division,
      members: division.members.map(member => ({
        ...member,
        profileImagePath: `/assets/administrator/${member.profile_image}`
      }))
    }));
    
    setDivisions(transformedData);
    
    // Initialize all divisions as expanded
    const initialExpandedState = {};
    transformedData.forEach((_, index) => {
      initialExpandedState[index] = true;
    });
    setExpandedDivisions(initialExpandedState);
    
    setLoading(false);
  }, []);

  const toggleDivision = (index) => {
    setExpandedDivisions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Calculate total members
  const totalMembers = divisions.reduce((acc, division) => acc + division.members.length, 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading administrators...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <Users className="w-10 h-10 mr-4" />
            <h1 className="text-4xl font-bold">நிர்வாகிகள்</h1>
          </div>
          <p className="text-orange-100 text-lg">
            மொத்தம் {totalMembers} நிர்வாகிகள்
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {divisions.length > 0 ? (
          <div className="space-y-8">
            {divisions.map((division, divisionIndex) => (
              <div 
                key={divisionIndex} 
                className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200"
              >
                {/* Division Header - Clickable */}
                <div 
                  className="bg-gradient-to-r from-orange-50 to-orange-100/50 p-6 cursor-pointer hover:from-orange-100 hover:to-orange-200/50 transition-all duration-300"
                  onClick={() => toggleDivision(divisionIndex)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 rtl:space-x-reverse">
                      <div className="bg-orange-600 text-white p-3 rounded-lg">
                        <MapPin className="w-6 h-6" />
                      </div>
                      <div>
                        <h2 className="text sm:text-1xl md:text-3xl font-semibold md:font-bold text-gray-800 leading-snug">
                          {division.team_division_title}
                        </h2>
                        <p className="text-orange-600 font-medium mt-1">
                          {division.members.length} நிர்வாகிகள்
                        </p>
                      </div>
                    </div>
                    <button className="text-orange-600 hover:text-orange-700 transition-colors">
                      {expandedDivisions[divisionIndex] ? (
                        <ChevronUp className="w-6 h-6" />
                      ) : (
                        <ChevronDown className="w-6 h-6" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Members Grid - Collapsible */}
                {expandedDivisions[divisionIndex] && (
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {division.members.map((member, memberIndex) => (
                        <div
                          key={member.mobile_number + memberIndex}
                          className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group border border-gray-100 flex flex-col"
                        >
                          {/* Image Section */}
                          <div className="relative overflow-hidden bg-gradient-to-br from-orange-100 to-orange-50">
                            <img
                              src={member.profileImagePath}
                              alt={member.name}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/assets/administrator/default.jpg';
                              }}
                            />
                            
                          </div>

                          {/* Content Section */}
                          <div className="p-5 flex-1 flex flex-col">
                            <h3 className="text sm:text-xl md:text-2xl 
                              font-semibold md:font-bold 
                              text-gray-800 mb-2 
                              leading-snug 
                              line-clamp-2">
                              {member.name}
                            </h3>
                            {/* Position Badge */}
                            <div className="top-4 right-4 mb-3 bg-orange-600 text-white lg:px-3 px-6 sm:px-6 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                              {member.team_division_position}
                            </div>
                            {/* Division Name */}
                            <div className="mb-4">
                              <span className="inline-block bg-gradient-to-r from-green-100 to-green-50 text-green-700 px-3 py-1.5 rounded-lg text-sm font-semibold border border-green-200">
                                {member.team_division}
                              </span>
                            </div>

                            {/* Contact Info */}
                            <div className="mt-auto pt-4 border-t border-gray-100">
                              <div className="flex items-center text-gray-700 hover:text-orange-600 transition-colors">
                                <Phone className="w-5 h-5 mr-3 text-orange-500 flex-shrink-0" />
                                <span className="text-base font-medium">
                                  {member.mobile_number}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-lg shadow-md">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">நிர்வாகிகள் இல்லை</h3>
            <p className="text-gray-500">தரவு ஏற்றப்படவில்லை</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Administrators;